import React from 'react'
import Typography, { TYPOGRAPHY_VARIANTS, FontWeight } from './Typography'

// Props di base per tutti i componenti tipografici
interface TextProps {
  children?: React.ReactNode
  content?: string | number
  className?: string
  weight?: FontWeight | undefined
  style?: React.CSSProperties
  dangerouslySetInnerHTML?: boolean
  as?: React.ElementType
  family?: 'sans' | 'serif'
  align?: 'left' | 'center' | 'right'
  htmlFor?: string
  id?: string
}

const baseProps = {
  align: 'left',
  family: 'sans',
  weight: undefined,
  className: undefined,
  style: undefined,
} as const

/**
 * Componente Display per grandi titoli o display
 */
const Display = ({
  children,
  content,
  size = 'lg',
  dangerouslySetInnerHTML = false,
  as = 'h2',
  ...props
}: TextProps & { size?: 'lg' | 'md' }) => {
  return (
    <Typography
      variant={TYPOGRAPHY_VARIANTS.DISPLAY}
      size={size}
      dangerouslySetInnerHTML={dangerouslySetInnerHTML}
      as={as}
      {...baseProps}
      {...props}
    >
      {children ? children : content}
    </Typography>
  )
}

/**
 * Componente Heading per titoli e intestazioni
 */
const Heading = ({
  children,
  content,
  size = 'lg',
  dangerouslySetInnerHTML = false,
  as,
  ...props
}: TextProps & { size?: 'xl' | 'lg' | 'md' | 'sm' | 'xs' }) => {
  return (
    <Typography
      variant={TYPOGRAPHY_VARIANTS.HEADLINE}
      size={size}
      dangerouslySetInnerHTML={dangerouslySetInnerHTML}
      as={as}
      {...baseProps}
      {...props}
    >
      {children ? children : content}
    </Typography>
  )
}

/**
 * Componente Paragraph per paragrafi di testo
 */
const Paragraph = ({
  children,
  content,
  size = 'md',
  dangerouslySetInnerHTML = false,
  ...props
}: TextProps & { size?: 'lg' | 'md' | 'sm' | 'xs' }) => {
  return (
    <Typography
      variant={TYPOGRAPHY_VARIANTS.PARAGRAPH}
      size={size}
      dangerouslySetInnerHTML={dangerouslySetInnerHTML}
      {...baseProps}
      {...props}
    >
      {children ? children : content}
    </Typography>
  )
}

/**
 * Componente Label per etichette
 */
const Label = ({
  children,
  content,
  size = 'md',
  dangerouslySetInnerHTML = false,
  ...props
}: TextProps & { size?: 'lg' | 'md' | 'xs' }) => {
  return (
    <Typography
      variant={TYPOGRAPHY_VARIANTS.LABEL}
      size={size}
      dangerouslySetInnerHTML={dangerouslySetInnerHTML}
      {...baseProps}
      {...props}
    >
      {children ? children : content}
    </Typography>
  )
}

/**
 * Componente ButtonLabel per etichette dei pulsanti
 */
const ButtonLabel = ({
  children,
  content,
  size = 'md',
  dangerouslySetInnerHTML = false,
  ...props
}: TextProps & { size?: 'md' | 'sm' }) => {
  return (
    <Typography
      variant={TYPOGRAPHY_VARIANTS.BUTTON}
      size={size}
      dangerouslySetInnerHTML={dangerouslySetInnerHTML}
      {...baseProps}
      {...props}
    >
      {children ? children : content}
    </Typography>
  )
}

/**
 * Componente Link per collegamenti
 */
const Link = ({
  children,
  content,
  size = 'md',
  dangerouslySetInnerHTML = false,
  ...props
}: TextProps & { size?: 'md' | 'sm' | 'xs' }) => {
  return (
    <Typography
      variant={TYPOGRAPHY_VARIANTS.LINK}
      size={size}
      dangerouslySetInnerHTML={dangerouslySetInnerHTML}
      {...baseProps}
      {...props}
    >
      {children ? children : content}
    </Typography>
  )
}

export { Display, Heading, Paragraph, Label, ButtonLabel, Link }
